/**
 * R9 WS-B N-e §4.10 — **observability retention**. Settings → Observability retention.
 *
 * | verb | path | producer `file:line` | permission |
 * |---|---|---|---|
 * | `GET` | `/v1/admin/settings/observability-retention` | `site-admin-api/.../observability-retention.controller.ts:37-38` | `cp:settings:read` |
 * | `PUT` | same | `:68-70` | `cp:observability:configure-retention` (WS-5 G4b / R-25) |
 *
 * ⚠️ The controller is a **proxy** — *"The canonical write happens in platform-api; this controller
 * proxies, but is also the audit point for the Settings workspace (so the audit trail mentions the
 * operator who clicked Save … not the service token)"* (`:20-24`). ⇒ the console must write through
 * **this** route and not platform-api's, or the audit row names a machine.
 *
 * # ⛔ ITS OWN MODULE, SEPARATE FROM `envConfig.api.ts` — a gate-integrity decision
 *
 * See `envConfig.api.ts`'s header for the full argument. In one line:
 * `permission-gate-parity.test.ts` is satisfied by **any one** of a module's declared permissions,
 * so a single module declaring both `cp:settings:read` and `cp:env-config:read` would let either
 * Settings screen discharge its gate obligation on the other screen's permission — which is the
 * exact conflation the old console shipped as a door onto a 403.
 *
 * # ⛔⛔ `PUT` WITH A `Partial` BODY IS THE TRAP ON THIS SCREEN
 *
 * The verb is `PUT` and the accepted body is `UpdateObservabilityRetentionDto`, every field
 * **optional** (`observability-retention.service.ts:27-32`). ⇒ **send only the fields the operator
 * actually changed.**
 *
 * ⚠️ Echoing all four back is a **read-modify-write**: it re-asserts three values the operator never
 * touched, over whatever changed underneath since the read — a lost update, silent, and it looks
 * harmless precisely because the values usually match. ⛔ The same defect `oauth-clients/api.ts`
 * documents for its own PATCH bodies (*"omission means leave alone, never reset to default"*).
 *
 * ⚠️ **What it is NOT, stated so the argument is not overclaimed:** it does not pollute the audit
 * trail. The controller computes `changedFields` from `Object.keys(dto)` but filters on `next !==
 * previous[key]` (`:52-59`), so an echoed-but-unchanged field is not recorded. The harm is the lost
 * update, full stop — and a precise reason survives review where an inflated one gets waved off.
 *
 * ⇒ `ObservabilityRetentionPane` tracks a **dirty set per field** and sends exactly it. The test
 * asserts it by reading the **echoed merged result** — an observable outcome — never a call count.
 */
import { api } from '../platform/http/client';

/** ⛔ Read off the producer (`:37-38`), per SR-1. Gates the ENTRY — see the manifest entry. */
export const SETTINGS_READ_PERMISSION = 'cp:settings:read';

/**
 * ⛔ The WRITE, and it gates **the Save control only, never the door**.
 *
 * ⚠️ Gating the manifest entry on this would hide the whole screen from an operator who can
 * legitimately read every retention window — the same rule `platform-alerts` already states. Read
 * opens the door; the write hides itself.
 *
 * # ⛔⛔ `cp:observability:configure-retention`, NOT `cp:settings:write` (WS-5 G4b / spine R-25)
 *
 * The producer is `observability-retention.controller.ts`'s `@Put()`, and R-25 aligned it onto
 * `cp:observability:configure-retention` to match the platform-api route it proxies. This constant
 * is read off that producer, per SR-1.
 *
 * ⭐ **AND NO TEST IN THIS PACKAGE WOULD HAVE CAUGHT IT IF I HAD LEFT IT ALONE — that is the whole
 * lesson, and it is worth more than this instance.** `shell/__tests__/permission-gate-parity.test.ts`
 * derives the required permission from **the console's own api-module constants**, not from the
 * producer; its stated property is *"a component … must gate on one of **that module's**
 * permissions"*. So a producer-side re-gate leaves it **green** while this constant is simply
 * wrong, and the screen renders a Save button that 403s — which is exactly what this pane's own
 * comment forbids (*"⛔ ABSENT without … — never a disabled Save. A control that 403s is worse"*).
 * That gate is honest about one ceiling in its docblock; **this is a second one it does not name.**
 * ⇒ the three-way ratchet lives OUTSIDE this package, in
 * `cloud/cp/test/__tests__/observability-retention-gate-parity.spec.ts` (`@zion/cp-parity-tests`),
 * because only that package may read both producer trees — cp-admin-ui must not reach into a
 * backend's `src`. Its `T3` asserts this ceiling as a property of `permission-gate-parity.test.ts`'s
 * INPUTS rather than leaving it as prose nothing checks. The console-side behaviour change is
 * `ObservabilityRetentionPane.test.tsx`'s `R4-R25` arm.
 */
export const SETTINGS_WRITE_PERMISSION = 'cp:observability:configure-retention';

export const OBSERVABILITY_RETENTION_PATH = '/v1/admin/settings/observability-retention';

/**
 * The four windows. `ObservabilityRetentionDto`, `observability-retention.service.ts:19-24`.
 *
 * ⛔ **The UNIT IS IN THE FIELD NAME AND MUST ALSO BE IN THE LABEL.** *"Retention: 30"* is
 * ambiguous, and a wrong unit on a retention window silently deletes telemetry — the one mistake on
 * this screen that cannot be undone by re-saving.
 */
export interface ObservabilityRetention {
  readonly dashboardRefreshSeconds: number;
  readonly mimirRetentionDays: number;
  readonly tempoRetentionDays: number;
  readonly lokiRetentionDays: number;
}

/**
 * The write body — every field optional, mirroring `UpdateObservabilityRetentionDto`.
 *
 * ⛔ **`Partial<>` is the point, not a convenience.** See the module header: omission means *leave
 * alone*, so the caller sends its dirty set and nothing else.
 */
export type UpdateObservabilityRetention = Partial<ObservabilityRetention>;

export function getObservabilityRetention(): Promise<ObservabilityRetention> {
  return api.get<ObservabilityRetention>(OBSERVABILITY_RETENTION_PATH);
}

/**
 * ⛔ **`PUT`, not `PATCH`** — the producer is `@Put()` (`observability-retention.controller.ts:43`).
 * ⚠️ A `PATCH` here would 404 at the gateway, which looks like a routing bug three layers from its
 * cause.
 *
 * @returns the **merged** result the producer echoes, so the caller re-renders from the server's
 *          answer rather than from what it hoped it sent.
 */
export function updateObservabilityRetention(
  body: UpdateObservabilityRetention,
): Promise<ObservabilityRetention> {
  return api.put<ObservabilityRetention>(OBSERVABILITY_RETENTION_PATH, body);
}
